import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CallStoreService } from '../../library/call-store/call-store.service';
import { AppConstants } from 'src/app/app-constants';
import { CallService } from 'src/app/services/learner/call.service';
import { TtsStreamService } from 'src/app/services/learner/tts-stream.service';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-quiz-result',
  standalone: false,
  templateUrl: './quiz-result.html',
  styleUrl: './quiz-result.scss',
})
export class QuizResult implements OnInit, OnChanges, OnDestroy {
  @Input() callStore: CallStoreService | undefined;
  @Input() outcome: string = AppConstants.QUIZ_PASSED_NEXT_STEP;
  @Input() ttsReady: boolean = false;
  @Input() stepsToReview: string[] = [];

  /** Emits the TTS script when outcome audio starts; null when it should clear from footer. */
  @Output() subtitleTextChange = new EventEmitter<string | null>();
  /** Emits true when outcome TTS starts, false when it ends (for footer "speaking" state). */
  @Output() ttsPlayingChange = new EventEmitter<boolean>();
  /** Emits the avatar thumbnail URL from the quiz result. */
  @Output() avatarThumbnailReady = new EventEmitter<string>();

  practiceCallUuid: string = '';
  callUuid: string = '';
  showResults: boolean = false;

  private speakingEndSubscription: Subscription | null = null;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly callService: CallService,
    private readonly ttsStreamService: TtsStreamService
  ) {}

  ngOnInit(): void {
    this.practiceCallUuid = this.callStore?.getPracticeCallUuid() ?? '';
    this.callUuid = this.callStore?.getCallUuid() ?? '';

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { practiceCallUuid: this.practiceCallUuid },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .then(() => {
        this.showResults = true;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ttsReady']?.currentValue === true && this.showResults) {
      this.playOutcomeAudio();
    }
  }

  private playOutcomeAudio(): void {
    const practiceCallUuid = this.practiceCallUuid?.trim();
    const textToConvert = (() => {
      if (this.stepsToReview.length > 0 || this.outcome === AppConstants.QUIZ_FAILED_PREVIOUS_STEP) {
        return "You didn't pass this quiz. Review the highlighted steps and try again.";
      } else if (this.outcome === AppConstants.QUIZ_PASSED_NEXT_STEP) {
        return 'Great job, you passed this quiz. Continue to the next step.';
      } else if (this.outcome === AppConstants.QUIZ_PASSED_LAST_STEP) {
        return 'Congratulations, you passed the final quiz. You can come back anytime to review the material or continue practicing.';
      } else if (this.outcome === AppConstants.QUIZ_FAILED_LAST_STEP) {
        return "You didn't pass this quiz. Review the highlighted material and try again.";
      } else if (this.outcome === AppConstants.QUIZ_FAILED_MAX_ATTEMPTS_REACHED) {
        return "You've used all your attempts. Please reach out to your manager to discuss next steps.";
      }
      return '';
    })();

    if (!practiceCallUuid || !textToConvert.trim()) {
      return;
    }

    this.subtitleTextChange.emit(textToConvert);
    this.ttsPlayingChange.emit(true);

    this.speakingEndSubscription?.unsubscribe();
    this.speakingEndSubscription = this.ttsStreamService.onSpeakingEnd$
      .pipe(take(1))
      .subscribe(() => {
        this.speakingEndSubscription = null;
        this.subtitleTextChange.emit(null);
        this.ttsPlayingChange.emit(false);
      });

    const stream$ = this.callService.getAudioStream(
      practiceCallUuid,
      textToConvert
    );
    this.ttsStreamService.playStream(stream$);
  }

  ngOnDestroy(): void {
    this.speakingEndSubscription?.unsubscribe();
    this.speakingEndSubscription = null;
  }
}
